import { Injectable } from '@angular/core';
import { BatchUploadStatus, PaymentStatus, ServiceRequestStatus, Status, TripStatus } from '@enums';
import { Actions, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';

interface Err {
  error: Error;
}

@Injectable({ providedIn: 'root' })
export class UtilService {
  constructor(private actions: Actions, private store: Store) {}

  public actionsToPromise<T>(
    action: Action,
    successAction: ActionCreator<any, (props: T) => T & Action<any>>,
    failedAction: ActionCreator<any, (props: Err) => Err & Action<any>>
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const subs: Subscription[] = [];

      const successSub = this.actions
        .pipe(ofType(successAction))
        .subscribe((result: T) => {
          for (const sub of subs) sub.unsubscribe();
          resolve(result);
        });

      const failedSub = this.actions
        .pipe(ofType(failedAction))
        .subscribe((err) => {
          for (const sub of subs) sub.unsubscribe();
          reject(err.error);
        });

      subs.push(successSub);
      subs.push(failedSub);
      this.store.dispatch(action);
    });
  }

  public toPromise<T>(observable: Observable<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      observable.subscribe({ next: resolve, error: reject });
    });
  }

  public isJwtTokenExpired(token: string, offsetSeconds = 30): boolean {
    const expirationDate = this.getJwtExpirationDate(token);
    if (!expirationDate) return false; // not expired if no expiry date

    const now = new Date().valueOf() + offsetSeconds * 1000;
    const expiry = expirationDate.valueOf();
    return now > expiry;
  }

  private getJwtExpirationDate(token: string) {
    const decoded = this.decodeToken(token);

    if (typeof decoded.exp === 'undefined') {
      return null;
    }

    const expirationDate = new Date(0);
    expirationDate.setUTCSeconds(decoded.exp);

    return expirationDate;
  }

  private decodeToken(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT Token, expecting 3 parts');
    }
    const decoded = this.urlBase64Decode(parts[1]);
    if (!decoded) {
      throw new Error('Cannot decode the token');
    }
    return JSON.parse(decoded);
  }

  private urlBase64Decode(input: string) {
    let output = input.replace(/-/g, '+').replace(/_/g, '/');
    switch (output.length % 4) {
      case 0:
        break;

      case 2:
        output += '==';
        break;

      case 3:
        output += '=';
        break;

      default:
        throw new Error('Illegal base64url string!');
    }
    return window.decodeURIComponent(escape(window.atob(output)));
  }

  public static formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  public capitalizeFirstLetter(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  public formatDate(date?: Date): string {
    if (!date) return '';

    let months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const d = new Date(date);
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }

  public getStatus(status: Status): string {
    if (status === Status.PENDING) return 'Pending';
    if (status === Status.APPROVED) return 'Approved';
    if (status === Status.DECLINED) return 'Declined';
    if (status === Status.EXPIRED) return 'Expired';
    return 'Unknown';
  }

  public getStatusIcon(status: Status): string {
    if (status === Status.PENDING) return 'pending';
    if (status === Status.APPROVED) return 'check_circle';
    if (status === Status.DECLINED) return 'cancel';
    if (status === Status.EXPIRED) return 'cancel';

    return '';
  }

  public getStatusColor(status: Status): string {
    if (status === Status.PENDING) return 'grey';
    if (status === Status.APPROVED) return 'green';
    if (status === Status.DECLINED) return 'red';
    if (status === Status.EXPIRED) return 'red';
    return '';
  }

  public getBatchUploadStatusIcon(status: BatchUploadStatus): string {
    if (status === BatchUploadStatus.PENDING) return 'pending';
    if (status === BatchUploadStatus.PROCESSING) return 'pending';
    if (status === BatchUploadStatus.UPLOADED) return 'pending';
    if (status === BatchUploadStatus.COMPLETE) return 'check_circle';
    if (status === BatchUploadStatus.VALIDATED) return 'check_circle';
    if (status === BatchUploadStatus.FAILED) return 'cancel';

    return '';
  }

  public getBatchUploadStatusColor(status: BatchUploadStatus): string {
    if (status === BatchUploadStatus.PENDING) return 'grey';
    if (status === BatchUploadStatus.PROCESSING) return 'grey';
    if (status === BatchUploadStatus.UPLOADED) return 'grey';
    if (status === BatchUploadStatus.COMPLETE) return 'green';
    if (status === BatchUploadStatus.VALIDATED) return 'green';
    if (status === BatchUploadStatus.FAILED) return 'red';
    return '';
  }

  public getPaymentStatusIcon(status: PaymentStatus): string {
    if (status === PaymentStatus.PENDING) return 'pending';
    if (status === PaymentStatus.PAID) return 'check_circle';
    if (status === PaymentStatus.PAID_WITH_ERRORS) return 'check_circle';
    if (status === PaymentStatus.FAILED) return 'cancel';

    return '';
  }

  public getPaymentStatusColor(status: PaymentStatus): string {
    if (status === PaymentStatus.PENDING) return 'grey';
    if (status === PaymentStatus.PAID_WITH_ERRORS) return 'grey';
    if (status === PaymentStatus.PAID) return 'green';
    if (status === PaymentStatus.FAILED) return 'red';
    return '';
  }

  getErrorMessage(err: any): string {
    if (err.error && err.error.message) {
      return err.error.message;
    } else if (err.message) {
      return err.message;
    } else {
      return 'An error occurred';
    }
  }

  public getTripStatus(status: TripStatus): string {
    if (status === TripStatus.PLANNED) return 'Planned';
    if (status === TripStatus.IN_PROGRESS) return 'In Progress';
    if (status === TripStatus.COMPLETED) return 'Completed';
    if (status === TripStatus.CANCELLED) return 'Cancelled';
    return 'Unknown';
  }

  public getTripStatusColor(status: TripStatus): string {
    if (status === TripStatus.PLANNED) return 'grey';
    if (status === TripStatus.IN_PROGRESS) return 'green';
    if (status === TripStatus.COMPLETED) return 'green';
    if (status === TripStatus.CANCELLED) return 'red';
    return 'Unknown';
  }

  public getServiceRequestStatus(status: ServiceRequestStatus): string {
    if (status === ServiceRequestStatus.REQUESTED) return 'Requested';
    if (status === ServiceRequestStatus.IN_PROGRESS) return 'In Progress';
    if (status === ServiceRequestStatus.COMPLETED) return 'Completed';
    if (status === ServiceRequestStatus.CANCELLED) return 'Cancelled';
    if (status === ServiceRequestStatus.ASSIGNED) return 'Assigned';
    if (status === ServiceRequestStatus.NO_DRIVERS_AVAILABLE) return 'No Drivers Available';
    return 'Unknown';
  }
    public getServiceRequestStatusColor(status: ServiceRequestStatus): string {
    if (status === ServiceRequestStatus.REQUESTED) return 'grey';
    if (status === ServiceRequestStatus.IN_PROGRESS) return 'green';
    if (status === ServiceRequestStatus.COMPLETED) return 'green';
    if (status === ServiceRequestStatus.CANCELLED) return 'red';
    if (status === ServiceRequestStatus.NO_DRIVERS_AVAILABLE) return 'red';
    return 'Unknown';
  }
}
